import React, { useMemo, JSX } from 'react';

import {
  ApiAdditionalInfoType,
  ApiHotel,
  ApiHotelRoom,
  ApiRoomRate,
  ApiTravelerType
} from '@ibe/api';
import { sanitize } from '@ibe/services';
import { BookedItemComponentProps } from '../../../Models/ItemRegistry';
import { Price } from '../../Components/UI/Price/Price';
import Dates from '../Dates';
import { Button, Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/summary.en.t.json';
import Keys from '../../../_Translations/generated/summary.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';

type Travelers = { adults: number; children: number; infants: number };

const Cart = (props: BookedItemComponentProps): JSX.Element => {
  const { t } = useTranslation('summary', fallback, Keys);
  const {
    item,
    booking,
    onDelete,
    onEdit,
    showPax,
    useCustomItemOrder,
    renderExternalContent
  } = props;
  const bookingTravelers = booking?.travelers;

  const price = item.price || null;

  const roomRate = booking.items.find(it => item.idParent === it.id) as ApiRoomRate;
  const room = booking.items.find(it => roomRate.idParent === it.id) as ApiHotelRoom;
  const hotel = booking.items.find(it => room.idParent === it.id) as ApiHotel;

  const resortFeeInformation = hotel?.additionalInfo?.find(
    v => v.infoType === ApiAdditionalInfoType.RESORTFEE
  )?.text;

  const travelers = useMemo(() => {
    return booking?.travelers?.reduce(
      (total: Travelers, current): Travelers => {
        return {
          adults: total.adults + (current.type === ApiTravelerType.ADULT ? 1 : 0),
          children: total.children + (current.type === ApiTravelerType.CHILD ? 1 : 0),
          infants: total.infants + (current.type === ApiTravelerType.INFANT ? 1 : 0)
        };
      },
      {
        adults: 0,
        children: 0,
        infants: 0
      }
    );
  }, [booking, bookingTravelers]);

  const renderEditDelete = (): JSX.Element => (
    <>
      <Col xs="auto" className="cart__edit d-flex justify-content-end align-items-end">
        {onEdit && (
          <Button
            outline
            color="secondary btn-sm"
            onClick={(): void => {
              onEdit(item.id);
            }}
          >
            <FontAwesomeIcon icon={faPenAlt} />
          </Button>
        )}
      </Col>
      <Col xs="auto" className="cart__delete d-flex justify-content-end align-items-end pl-0">
        {onDelete && (
          <Button
            outline
            color="primary btn-sm"
            onClick={(): void => {
              onDelete(item.id);
            }}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Button>
        )}
      </Col>
    </>
  );

  return (
    <>
      {useCustomItemOrder && (
        <div className="cart__buttons__container d-flex">{renderEditDelete()}</div>
      )}
      <Row>
        <Col className="cart__row__container">
          {!!renderExternalContent && renderExternalContent}
          <Row className="cart__row__name align-items-center">
            <Col className="cart__name">
              <h6 className="my-0">{item.name}</h6>
            </Col>
            {!useCustomItemOrder && renderEditDelete()}
          </Row>
          {showPax && (travelers.adults > 0 || travelers.children > 0 || travelers.infants > 0) && (
            <Row className="cart__row__pax">
              <Col>{`${
                travelers.adults > 0
                  ? `${travelers.adults} ${t(Keys.adult, { count: travelers.adults })}`
                  : ''
              }${
                travelers.adults > 0 && (travelers.children > 0 || travelers.infants > 0)
                  ? `, `
                  : ''
              }${
                travelers.children > 0
                  ? `${travelers.children} ${t(Keys.child, { count: travelers.children })}`
                  : ''
              }${travelers.children > 0 && travelers.infants > 0 ? `, ` : ''}${
                travelers.infants > 0
                  ? `${travelers.infants} ${t(Keys.infant, { count: travelers.infants })}`
                  : ''
              }`}</Col>
            </Row>
          )}
          <Row className="cart__row__date p-0">
            <Col className="d-flex py-0">
              <Dates item={item} />
            </Col>
            {!resortFeeInformation && !useCustomItemOrder && (
              <Col className="item-cart d-flex align-items-center justify-content-end ">
                <span className="text-muted ml-1">{price && <Price price={price} />}</span>
              </Col>
            )}
          </Row>
          <Row className="cart__row__room align-items-center">
            <Col>
              {room && room.name && (
                <div dangerouslySetInnerHTML={{ __html: sanitize(room.name) }} />
              )}
            </Col>
          </Row>
          {resortFeeInformation && (
            <Row className="cart__row__resort-fee">
              <Col className="d-flex align-items-center justify-content-between">
                <span className="font-weight-bold">{resortFeeInformation}</span>
                {!useCustomItemOrder && (
                  <span className="text-muted ml-1">{price && <Price price={price} />}</span>
                )}
              </Col>
            </Row>
          )}
        </Col>
        {useCustomItemOrder && (
          <Row className="cart__row__price d-flex">
            <Col className="d-flex align-items-center justify-content-end">
              <span className="text-muted ml-1">{price && <Price price={price} />}</span>
            </Col>
          </Row>
        )}
      </Row>
    </>
  );
};

export default Cart;
